// The one chat box on the web (TOM-671): a rounded capsule with + inside on
// the left, a text field that grows, the mic, Neo's Talk where a chat offers a
// voice call (TOM-731), and the send arrow, which turns into Stop while Neo
// answers. Every chat uses it, each with its own
// placeholder: the Neo page, the home chat, the Neo panel, the Watch and the
// admin's Neo change chat.
//
// Photos and files come in three ways, all through useChatAttachments: the +
// menu's picker, a paste, and a drop on the box. They wait as small tiles in
// the capsule above the text, uploading as soon as they are added. A chat
// that cannot carry files (the admin's) passes no `attachments`, and then the
// box takes none.
//
// Up and Down step through the messages the owner sent in the open chat
// (TOM-915), which each chat passes as `history`; lib/inputHistory has the rules.

import { useDemoGuest } from '../auth';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  CHAT_ATTACHMENT_ACCEPT,
  chatAttachmentSrc,
  filesFromTransfer,
} from '../lib/chatAttachments';
import {
  INPUT_HISTORY_START,
  caretOnFirstLine,
  caretOnLastLine,
  historyBack,
  historyEntries,
  historyExit,
  historyForward,
  type InputHistoryStep,
} from '../lib/inputHistory';
import type { ChatAttachments, PendingChatAttachment } from '../lib/useChatAttachments';
import { useDictation } from '../lib/useDictation';

/** One more line in the + menu, such as "New chat". */
export interface ChatComposerMenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  testId?: string;
}

export interface ChatComposerProps {
  value: string;
  onChange: (next: string) => void;
  /** Send what is in the box. Called only when it can be sent. */
  onSubmit: () => void;
  placeholder: string;
  /** True while Neo answers: the send arrow is Stop, or waits when there is no `onStop`. */
  busy?: boolean;
  /** Stop the answer in progress. */
  onStop?: () => void;
  /** True while the chat cannot send yet (being read back, say). */
  blocked?: boolean;
  /** The field cannot be typed in at all (the Watch with no AI configured). */
  disabled?: boolean;
  /** Keep typing while Neo answers, but never send meanwhile. */
  readOnlyWhileBusy?: boolean;
  attachments?: ChatAttachments;
  menuItems?: readonly ChatComposerMenuItem[];
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  ariaLabel?: string;
  describedBy?: string;
  maxLength?: number;
  className?: string;
  testId?: string;
  /** Drawn after the mic: Neo's Talk button (TOM-731). */
  talk?: ReactNode;
  /** Drawn above the box: a voice call's strip (TOM-731). */
  above?: ReactNode;
  /** False hides the dictation mic, as during a voice call. */
  dictate?: boolean;
  /**
   * The words of the messages the owner sent in this chat, oldest first. Up and
   * Down step through them (TOM-915).
   */
  history?: readonly string[];
}

const MAX_FIELD_HEIGHT = 168;

function placeCaret(field: HTMLTextAreaElement, caret: InputHistoryStep['caret']) {
  const at = caret === 'start' ? 0 : field.value.length;
  field.setSelectionRange(at, at);
  if (caret === 'start') field.scrollTop = 0;
}

export function ChatComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  busy = false,
  onStop,
  blocked = false,
  disabled = false,
  readOnlyWhileBusy = false,
  attachments: attachmentsProp,
  menuItems = [],
  inputRef,
  ariaLabel = 'Message Neo',
  describedBy,
  maxLength = 8_000,
  className,
  testId,
  talk,
  above,
  dictate = true,
  history,
}: ChatComposerProps) {
  // A Try it guest stores no files (TOM-868): the server refuses uploads, so
  // the + menu, paste and drop take none.
  const attachments = useDemoGuest() ? undefined : attachmentsProp;
  const ownRef = useRef<HTMLTextAreaElement | null>(null);
  const fieldRef = inputRef ?? ownRef;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const dictation = useDictation(value, onChange);
  const noticeId = useId();
  const entries = useMemo(() => historyEntries(history ?? []), [history]);
  // Where the box is in its history, and where the caret goes once a recalled
  // message is in the field.
  const historyRef = useRef(INPUT_HISTORY_START);
  const caretRef = useRef<InputHistoryStep['caret'] | null>(null);
  // A new message sent, or another chat opened: the next Up starts from the
  // newest message. Keyed on the words, not the array, which a parent may
  // rebuild on every render.
  const entriesKey = entries.join('\u0000');
  useEffect(() => {
    historyRef.current = INPUT_HISTORY_START;
  }, [entriesKey]);
  useLayoutEffect(() => {
    const caret = caretRef.current;
    const field = fieldRef.current;
    if (caret === null || field === null) return;
    caretRef.current = null;
    placeCaret(field, caret);
  }, [value, fieldRef]);

  const items = attachments?.items ?? [];
  const hasText = value.trim().length > 0;
  const hasFiles = items.length > 0;
  const failed = items.some((i) => i.status === 'failed');
  // The server takes a message only with words in it (§12.2), so a photo goes
  // with a line about it.
  const canSend =
    hasText && !busy && !blocked && !disabled && !(attachments?.uploading ?? false) && !failed;
  const showStop = busy && onStop !== undefined;

  // The field grows with what is typed, up to a few lines, then scrolls.
  useEffect(() => {
    const field = fieldRef.current;
    if (field === null) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, MAX_FIELD_HEIGHT)}px`;
    field.style.overflowY = field.scrollHeight > MAX_FIELD_HEIGHT ? 'auto' : 'hidden';
  }, [value, fieldRef]);

  // A voice call takes the microphone: dictation in progress stops.
  const stopDictation = dictation.stop;
  useEffect(() => {
    if (!dictate && dictation.listening) stopDictation();
  }, [dictate, dictation.listening, stopDictation]);

  function submit() {
    if (!canSend) return;
    if (dictation.listening) dictation.stop();
    historyRef.current = INPUT_HISTORY_START;
    onSubmit();
  }

  function onFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submit();
  }

  // Up on the first line goes back through what the owner sent, Down on the
  // last line comes forward to the draft, and Escape goes straight to the
  // draft. Anywhere else the arrows move the caret as usual. A field that is
  // read-only while Neo answers keeps its lock.
  function onHistoryKey(event: KeyboardEvent<HTMLTextAreaElement>): boolean {
    const { key, altKey, ctrlKey, metaKey, shiftKey } = event;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'Escape') return false;
    if (event.nativeEvent.isComposing || altKey || ctrlKey || metaKey || shiftKey) return false;
    if (disabled || (readOnlyWhileBusy && busy)) return false;
    const field = event.currentTarget;
    const { selectionStart, selectionEnd } = field;
    let step: InputHistoryStep | null = null;
    if (key === 'Escape') {
      step = historyExit(historyRef.current);
    } else if (selectionStart !== selectionEnd) {
      return false;
    } else if (key === 'ArrowUp') {
      if (!caretOnFirstLine(value, selectionStart)) return false;
      step = historyBack(entries, historyRef.current, value);
    } else {
      if (!caretOnLastLine(value, selectionEnd)) return false;
      step = historyForward(entries, historyRef.current);
    }
    if (step === null) return false;
    event.preventDefault();
    // Escape returns to the draft here; it does not also close the chat.
    if (key === 'Escape') event.stopPropagation();
    historyRef.current = step.state;
    if (step.value === value) {
      placeCaret(field, step.caret);
    } else {
      caretRef.current = step.caret;
      onChange(step.value);
    }
    return true;
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (onHistoryKey(event)) return;
    // Enter sends, Shift+Enter is a new line, and Enter while composing a
    // character (Japanese, Chinese) finishes the character.
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  }

  // A pasted photo or file joins the message; pasted text goes in the field.
  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    if (attachments === undefined) return;
    const files = filesFromTransfer(event.clipboardData);
    if (files.length === 0) return;
    event.preventDefault();
    attachments.add(files);
  }

  const takesDrops = attachments !== undefined && !disabled;
  function onDragOver(event: DragEvent<HTMLFormElement>) {
    if (!takesDrops || !Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }
  function onDragLeave(event: DragEvent<HTMLFormElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setDragging(false);
  }
  function onDrop(event: DragEvent<HTMLFormElement>) {
    if (!takesDrops) return;
    event.preventDefault();
    setDragging(false);
    const files = filesFromTransfer(event.dataTransfer);
    if (files.length > 0) attachments!.add(files);
    fieldRef.current?.focus();
  }

  const allMenuItems: ChatComposerMenuItem[] = [
    ...(attachments === undefined
      ? []
      : [
          {
            key: 'attach',
            label: 'Add photos or files',
            icon: <PaperclipIcon />,
            onSelect: () => fileRef.current?.click(),
            testId: 'chat-composer-attach',
          },
        ]),
    ...menuItems,
  ];

  const notice = attachments?.notice ?? dictation.error;
  const sendTitle = !hasText && hasFiles ? 'Add a few words to send with it' : 'Send';

  return (
    <div className={`chat-composer${className === undefined ? '' : ` ${className}`}`}>
      {above}
      <form
        className={`chat-composer__box${dragging ? ' chat-composer__box--drop' : ''}${disabled ? ' chat-composer__box--disabled' : ''}`}
        onSubmit={onFormSubmit}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={testId}
      >
        {hasFiles ? (
          <ul className="chat-composer__tray" aria-label="Attached photos and files">
            {items.map((item) => (
              <AttachmentTile key={item.key} item={item} onRemove={attachments!.remove} />
            ))}
          </ul>
        ) : null}
        <div className="chat-composer__row">
          {allMenuItems.length > 0 ? (
            <ComposerPlus items={allMenuItems} disabled={disabled} />
          ) : (
            <span className="chat-composer__inset" aria-hidden="true" />
          )}
          <textarea
            ref={fieldRef}
            className="chat-composer__field"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={hasFiles && !hasText ? 'Add a message' : placeholder}
            aria-label={ariaLabel}
            aria-describedby={
              [describedBy, notice !== null ? noticeId : undefined].filter(Boolean).join(' ') ||
              undefined
            }
            rows={1}
            maxLength={maxLength}
            disabled={disabled}
            // readOnly, not disabled: a disabled field drops focus, and the
            // owner should keep typing the next message while Neo answers.
            readOnly={readOnlyWhileBusy && busy}
          />
          {dictate && dictation.supported && !disabled ? (
            <button
              type="button"
              className={`chat-composer__icon chat-composer__mic${dictation.listening ? ' chat-composer__mic--on' : ''}`}
              aria-label={dictation.listening ? 'Stop dictation' : 'Dictate'}
              aria-pressed={dictation.listening}
              title={dictation.listening ? 'Stop dictation' : 'Dictate'}
              onClick={() => {
                dictation.toggle();
                fieldRef.current?.focus();
              }}
              data-testid="chat-composer-mic"
            >
              <MicIcon />
            </button>
          ) : null}
          {disabled ? null : talk}
          {showStop ? (
            <button
              type="button"
              className="chat-composer__icon chat-composer__stop"
              aria-label="Stop Neo"
              title="Stop"
              onClick={onStop}
              data-testid="chat-composer-stop"
            >
              <StopIcon />
            </button>
          ) : hasText || hasFiles || busy ? (
            <button
              type="submit"
              className="chat-composer__send"
              aria-label="Send message"
              title={sendTitle}
              disabled={!canSend}
              data-testid="chat-composer-send"
            >
              <ArrowUpIcon />
            </button>
          ) : null}
        </div>
        {attachments !== undefined ? (
          <input
            ref={fileRef}
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            accept={CHAT_ATTACHMENT_ACCEPT}
            multiple
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              // The same file can be picked again after it is removed.
              event.target.value = '';
              if (files.length > 0) attachments.add(files);
              fieldRef.current?.focus();
            }}
            data-testid="chat-composer-file"
          />
        ) : null}
      </form>
      {notice !== null ? (
        <p className="chat-composer__notice" id={noticeId} role="status">
          {notice}
          {attachments?.notice != null ? (
            <button
              type="button"
              className="chat-composer__notice-close"
              aria-label="Dismiss"
              onClick={attachments.dismissNotice}
            >
              <CloseIcon />
            </button>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

function AttachmentTile({
  item,
  onRemove,
}: {
  item: PendingChatAttachment;
  onRemove: (key: string) => void;
}) {
  const label =
    item.status === 'uploading'
      ? `${item.name}, uploading`
      : item.status === 'failed'
        ? `${item.name}, not uploaded`
        : item.name;
  return (
    <li
      className={`chat-composer__tile chat-composer__tile--${item.kind} chat-composer__tile--${item.status}`}
      title={item.error ?? item.name}
      data-testid="chat-composer-tile"
    >
      {item.kind === 'image' && (item.previewUrl !== null || item.uploaded !== null) ? (
        <img src={item.previewUrl ?? chatAttachmentSrc(item.uploaded!.attachmentId)} alt="" />
      ) : (
        <span className="chat-composer__tile-file">
          <FileIcon />
          <span className="chat-composer__tile-name">{item.name}</span>
        </span>
      )}
      <span className="sr-only">{label}</span>
      {item.status === 'uploading' ? (
        <span className="chat-composer__tile-spinner" aria-hidden="true" />
      ) : null}
      {item.status === 'failed' ? (
        <span className="chat-composer__tile-error" role="alert">
          {item.error}
        </span>
      ) : null}
      <button
        type="button"
        className="chat-composer__tile-remove"
        aria-label={`Remove ${item.name}`}
        title="Remove"
        onClick={() => onRemove(item.key)}
      >
        <CloseIcon />
      </button>
    </li>
  );
}

/**
 * The + button and its menu, opening upward. With one item and nothing to
 * choose, the + does it straight away: the picker opens.
 */
function ComposerPlus({
  items,
  disabled,
}: {
  items: readonly ChatComposerMenuItem[];
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const single = items.length === 1 ? items[0]! : null;

  useEffect(() => {
    if (!open) return;
    firstItemRef.current?.focus();
    function onPointerDown(event: PointerEvent) {
      if (wrapRef.current?.contains(event.target as Node) === true) return;
      setOpen(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="chat-composer__plus" ref={wrapRef}>
      {open ? (
        <div className="tb-menu chat-composer__menu" role="menu" aria-label="Add to chat">
          {items.map((item, i) => (
            <button
              key={item.key}
              ref={i === 0 ? firstItemRef : undefined}
              type="button"
              role="menuitem"
              className="tb-menu__item"
              data-testid={item.testId}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        className="chat-composer__icon"
        aria-label={single !== null ? single.label : 'Add to chat'}
        aria-haspopup={single !== null ? undefined : 'menu'}
        aria-expanded={single !== null ? undefined : open}
        title={single !== null ? single.label : 'Add photos, files and more'}
        disabled={disabled}
        onClick={() => (single !== null ? single.onSelect() : setOpen((value) => !value))}
        data-testid="chat-composer-plus"
      >
        <PlusIcon />
      </button>
    </div>
  );
}

// Lucide glyphs, drawn with the stroke the stylesheet gives them.
function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
      <path d="M12 18v4" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </svg>
  );
}

/** Stop: an outlined circle around a small filled square. */
function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9.5" />
      <rect className="chat-composer__stop-square" x="8.5" y="8.5" width="7" height="7" rx="1.25" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
